<ng-container *transloco="let t">
  <div class="mx-auto w-full max-w-screen-md pt-10">
    <div class="mb-10">
      <div class="text-2xl font-semibold tracking-tight text-gray-800">{{ t("page-title.billing") }}</div>
      <div class="text-sm font-medium text-gray-500">{{ t("billing-settings.billing-page-subtitle") }}</div>
    </div>

    <div class="mb-5">
      <ISelect
        [options]="organizationOptions()"
        [selectedValue]="selectedOrganization()"
        by="id"
        (selectedOptionChange)="changeOrganization($event.value)"
        searchable
      />
    </div>
    @if (selectedOrganization()) {
      <div class="mb-5 pb-3">
        <div class="flex items-center gap-7">
          <div>
            <div class="text-sm font-medium text-gray-800">{{ t("account-setting.organization") }}</div>
            <div class="text-sm text-gray-500">{{ selectedOrganization()!.organizationName }}</div>
          </div>
          @if (selectedOrganization()!.customer) {
            <div>
              <div class="text-sm font-medium text-gray-800">{{ t("billing-settings.billing_owner") }}</div>
              <div class="text-sm text-gray-500">{{ selectedOrganization()!.customer!.m19Email }}</div>
            </div>
          }
          @if (canDisplayBillingInfo()) {
            <div>
              <div class="text-sm font-medium text-gray-800">{{ t("subscription-card.po_number") }}</div>
              @if (poNumber()) {
                <div class="flex items-center gap-2">
                  <div class="text-sm text-gray-500">{{ poNumber() }}</div>
                  <IButton
                    icon="icon-[lucide--edit-3]"
                    color="gray"
                    variant="ghost"
                    size="sm"
                    (onClick)="openUpdatePoNumberModal()"
                  />
                </div>
              } @else {
                <IButton [label]="t('common.add')" color="white" size="sm" (onClick)="openUpdatePoNumberModal()" />
              }
            </div>
          }
        </div>

        <div class="my-7">
          @if (selectedOrganization()!.organization.billingPlan && locale()) {
            <lib-plan-card
              [organizationGroup]="selectedOrganization()!"
              [accountMarketplaces]="accountMarketplaces()"
              [locale]="locale()"
              [currency]="currency()"
              [totalAdSpendFees]="totalOrganizationAdSpendFees()"
              [dailyHourlyKeywords]="organizationKeywordMap.get(selectedOrganization()!.organization.organizationId!)"
              (accountSpendCsv)="exportAccountSpendCsv()"
            >
              <div cardActions class="flex items-center">
                @if (canMigrateToParentOrganization()) {
                  <IButton
                    color="white"
                    size="sm"
                    [label]="t('billing-settings.migrate_to', [parentOrgName()])"
                    (onClick)="openMigrateToParentOrganizationModal()"
                  />
                  <onboarding-cta></onboarding-cta>
                }
              </div>
            </lib-plan-card>
          }
        </div>

        <hr class="mb-5 h-px w-full border-gray-200" />
      </div>
      @if (selectedOrganization()?.customer && !isSelfService()) {
        <div class="mb-3 flex items-center justify-between">
          <div class="mb-3 text-xl font-semibold tracking-tight text-gray-800">
            {{ t("billing-registration-payment.payment_methods") }}
          </div>

          @if (isOrgOwner()) {
            <IButton
              [label]="t('billing-settings.add_credit_card')"
              color="white"
              icon="icon-[lucide--plus]"
              size="sm"
              (onClick)="openAddCreditCardModal()"
            />
          }
        </div>
        <div>
          <lib-credit-cards
            [customer]="selectedOrganization()!.customer!"
            (onSetAsDefaultCreditCard)="setAsDefaultCreditCard($event)"
            (onDeleteCreditCard)="deleteCreditCard($event)"
          />
        </div>
      }

      @if (!isSelfService()) {
        <div class="mt-10">
          <div class="mb-3 text-xl font-semibold tracking-tight text-gray-800">
            {{ t("billing-settings.invoices_history") }}
          </div>
          @if (this.nextInvoice()?.nextCycleDate) {
            <div class="mb-3 text-sm font-medium text-gray-500">
              {{
                t("billing-settings.next_invoice_date", {
                  date: this.nextInvoice()?.nextCycleDate,
                })
              }}
            </div>
          }
          <div class="mx-auto max-h-[25rem] w-[95rem] max-w-full">
            <lib-invoices-table
              [customer]="selectedOrganization()!.customer!"
              [locale]="locale()"
              [dailyAdSpendFeesByInvoiceId]="feesByInvoiceId()"
              (invoiceInfoClick)="openInvoiceDetailsModal($event)"
            />
          </div>
        </div>
      } @else if (this.nextInvoice()?.nextCycleDate) {
        <!-- in case there is no invoice but a next invoice is coming  -->
        <div class="mb-3 text-sm font-medium text-gray-500">
          {{
            t("billing-settings.next_invoice_date", {
              date: this.nextInvoice()?.nextCycleDate,
            })
          }}
        </div>
      }

      @if (canDisplayBillingInfo()) {
        <div class="mt-10">
          <div class="flex items-center justify-between">
            <div class="mb-3 text-xl font-semibold tracking-tight text-gray-800">
              {{ t("billing-settings.billing_info") }}
            </div>
            @if (isOrgOwner() && selectedOrganization()!.customer) {
              <div class="mb-3 flex justify-end">
                <IButton
                  color="white"
                  size="sm"
                  [label]="t('billing-settings.update_billing_info')"
                  icon="icon-[lucide--edit-3]"
                  (onClick)="openUpdateBillingDetailsModal()"
                />
              </div>
            }
          </div>

          <div class="mx-auto max-h-[25rem] w-[95rem] max-w-full">
            @if (selectedOrganization()!.customer) {
              <lib-billing-info
                [customer]="selectedOrganization()!.customer!"
                [stripePublicKey]="stripePublishableKey"
              />
            } @else if (isOrgOwner()) {
              <IButton
                [label]="t('billing-settings.setup_your_billing_info')"
                size="sm"
                color="white"
                (onClick)="openUpdateBillingDetailsModal()"
              />
            }
          </div>
        </div>
      }
    }
  </div>

  <ng-template #updatePoNumberModal>
    <div class="w-[300px]">
      <IInput size="sm" [value]="poNumber() ?? null" (valueChange)="poNumberInput.set($event)" />

      <div class="flex justify-end gap-2 pt-4">
        <IButton [label]="t('common.cancel')" color="white" size="sm" (onClick)="updatePoNumberModalRef?.close()" />
        <IButton
          [label]="t('common.delete')"
          color="red"
          size="sm"
          [disabled]="poNumberInput() === null"
          [loading]="poNumberLoading()"
          (onClick)="updatePoNumber(true)"
        />
        <IButton
          [label]="t('common.save')"
          size="sm"
          [disabled]="poNumberInput() === null"
          [loading]="poNumberLoading()"
          (onClick)="updatePoNumber()"
        />
      </div>
    </div>
  </ng-template>

  <ng-template #updateBillingDetailsModal>
    <div class="w-[35rem]">
      @if (customerLoading()) {
        <div class="absolute inset-0 top-0 z-10 flex h-full w-full items-center justify-center bg-white/50">
          <ISpinner [display]="true" />
        </div>
      }

      <lib-billing-info-form
        [stripePublishableKey]="stripePublishableKey"
        [formValue]="selectedOrganization()!.customer!"
        [requiredCard]="false"
        [updateBillingMode]="true"
        (billingFormValueChange)="updateBillingDetails($event)"
        (onCancel)="modalRef?.close()"
      />
    </div>
  </ng-template>
</ng-container>
